<!--
// nuxt-ui/pages/[region]/Tables.vue
-->
<script setup lang="ts">
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import type {
  StandingPlayoffColors,
  StandingPositionColor,
} from "~/src/season-cluster/domain/models/01.Season";
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import { useSeasonFilter } from "~/nuxt-ui/Shared/composables/season/seasonFilter";
import { useSeasons } from "~/nuxt-ui/Shared/composables/season/seasons";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import SeasonsFilter from "~/nuxt-ui/components/Shared/SeasonsFilter.vue";
import BaseBackdropLoading from "~/nuxt-ui/components/base/BaseBackdropLoading.vue";
import CurrentSeasonSliderMatches from "~/nuxt-ui/components/Shared/slider-matches/CurrentSeasonSliderMatches.vue";
import GridPartnersCompetition from "~/nuxt-ui/components/Shared/GridPartnersCompetition.vue";
import HeaderBannerTitle from "~/nuxt-ui/components/Shared/HeaderBannerTitle.vue";
import StandingsLegend from "~/nuxt-ui/components/Shared/standings/StandingsLegend.vue";
import BracketTablesWorldCup from "~/nuxt-ui/components/pages/region-world-cup/BracketTablesWorldCup.vue";
import SliderArrowsBehaviour from "~/nuxt-ui/components/Shared/slider-matches/SliderArrowsBehaviour.vue";
import BracketTablesNations from "~/nuxt-ui/components/pages/region-nations/BracketTablesNations.vue";
import KwcTablesAllLeagues from "~/nuxt-ui/components/pages/region-world-cup/KwcTablesAllLeagues.vue";
import GermanyBracket from "~/nuxt-ui/components/pages/region-home/GermanyBracket.vue";

const localePath = useLocalePath();

const { t } = useI18n();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { isWorldCup, isNations, isSpain, isAmericas, isGermany } = useRegions();
const { region, currentSeason } = useCompetitionStore();
const { getSeasonStanding } = useSeasons();
const { contextSiteTitle, isKings, isQueens } = useWebContext();

const standingPhase = ref<Phase>();
const loading = ref<boolean>(false);
const positionLegend = ref<StandingPlayoffColors[]>();
const secondaryPositionLegend = ref<StandingPlayoffColors[]>();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/tables",
    es: "/[region]/clasificacion",
    de: "/[region]/klassifikation",
    fr: "/[region]/classement",
    it: "/[region]/classifica",
    pt: "/[region]/classificacao",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const getRowColor = (i: number): string | undefined => {
  const colors = positionLegend.value;
  const playoffPosition = colors ? colors[i] : undefined;
  return getRowPositionColorTailwindClass(playoffPosition?.color);
};

const getSecondaryRowColor = (i: number): string | undefined => {
  const colors = secondaryPositionLegend.value;
  const secondaryPlayoffPosition = colors ? colors[i] : undefined;
  return getSecondaryRowPositionColorTailwindClass(secondaryPlayoffPosition?.color);
};

async function updateStandingPhase(): Promise<Phase | void> {
  try {
    loading.value = true;
    const _seasonId = seasonId.value;
    if (!_seasonId) return;
    const {
      phase,
      positionLegend: legend,
      secondaryPositionLegend: secondaryLegend,
    } = await getSeasonStanding(_seasonId);
    standingPhase.value = phase;
    positionLegend.value = legend;
    secondaryPositionLegend.value = secondaryLegend;
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

const isKingsFirstSplit = computed(() => {
  return seasonId.value === 3;
});

function getRowPositionColorTailwindClass(playoffColor: StandingPositionColor | undefined) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
      return "!bg-emerald-600";
    case "orange":
      return "!bg-amber-400";
    case "softOrange":
      return "!bg-[#EC7B39]";
    case "red":
      return "!bg-red-500";
    case "yellow":
      return "!bg-yellow-200";
    case "brown":
      return "!bg-[#A8834E]";
    case "gray":
      return "!bg-gray-500";
    default:
      return "!bg-gray-800";
  }
}

function getSecondaryRowPositionColorTailwindClass(playoffColor: StandingPositionColor | undefined) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
      return "before:!bg-emerald-600";
    case "orange":
      return "before:!bg-amber-400";
    case "softOrange":
      return "before:!bg-[#EC7B39]";
    case "red":
      return "before:!bg-red-500";
    case "yellow":
      return "before:!bg-yellow-200";
    case "brown":
      return "before:!bg-[#A8834E]";
    case "gray":
      return "before:!bg-gray-500";
    default:
      return "before:!bg-gray-800";
  }
}

if (seasonId.value) {
  await updateStandingPhase();
}

useSeoMeta({
  title: t("header.navCompetition.tables"),
  ogTitle: t("header.navCompetition.tables"),
  description: t("meta.description.tables.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.tables.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <header-banner-title :title="$t('tables.banner.title')"></header-banner-title>

  <section v-if="!isWorldCup && !isNations && !isGermany" class="kql-w-container px-4">
    <div class="flex flex-col bigsm:flex-row items-center justify-start gap-2 bigsm:justify-start">
      <nuxt-link
        v-if="seasonClusterId === currentSeason.seasonClusterId && isKings && (isSpain || isAmericas)"
        :to="localePath({ name: 'region-Tables', params: { region: 'world-cup' } })"
        class="btn btn-primary w-full bigsm:w-auto text-center text-sm min-w-max"
        external
        >Road to World Cup</nuxt-link
      >
      <nuxt-link
        v-else-if="seasonClusterId === currentSeason.seasonClusterId && isQueens"
        :to="localePath({ name: 'FinalissimaQueens' })"
        class="btn btn-primary w-full bigsm:w-auto text-center text-sm min-w-max"
        external
        >Road to Finalissima</nuxt-link
      >
      <div class="w-full bigsm:ml-auto">
        <seasons-filter
          v-model:season-id="seasonId"
          v-model:season-cluster-id="seasonClusterId"
          @change="updateStandingPhase"
        />
      </div>
    </div>

    <div v-if="standingPhase" class="relative">
      <base-backdrop-loading :active="loading"></base-backdrop-loading>

      <div v-for="(groupStanding, i) in standingPhase.groups" :key="groupStanding.id">
        <div class="flex justify-end md:justify-start text-gray-400 uppercase text-sm mb-2">
          <span v-if="standingPhase.groups.length > 1" class="standing-group-title"
            >{{ $t("general.group.word") }}
            <span class="standing-group-name">{{ groupStanding.name }}</span></span
          >
        </div>
        <hr v-if="standingPhase.groups.length > 1" />
        <div
          id="standingDesktop"
          class="bg-gray-900 p-4 rounded-lg my-3 relative overflow-x-scroll hide-scrollbar"
        >
          <div
            class="container-standing-rows overflow-x-scroll hide-scrollbar"
            :class="i === 0 && standingPhase.groups.length > 1 ? 'mb-4' : 'mb-0'"
          >
            <div class="header-table-standing">
              <div class="header-standing-team">
                <span class="text-left">{{ $t("tables.position.ab") }}</span>
                <span class="col-start-2 col-span-3 pl-4">{{ $t("tables.team.word") }}</span>
              </div>
              <div class="header-standing-data">
                <span v-if="!isKingsFirstSplit" class="header-title-item">{{ $t("tables.points.ab") }}</span>
                <span class="header-title-item">{{ $t("tables.gamesPlayed.ab") }}</span>
                <span :class="['header-title-item', { 'order-first': isKingsFirstSplit }]">
                  {{ $t("tables.wins.ab") }}
                </span>
                <span class="header-title-item">{{ $t("tables.penaltyWins.ab") }}</span>
                <span class="header-title-item">{{ $t("tables.penaltyLoses.ab") }}</span>
                <span class="header-title-item">{{ $t("tables.lost.ab") }}</span>
                <span class="header-title-item">{{ $t("tables.goalsPro.ab") }}</span>
                <span class="header-title-item">{{ $t("tables.goalsAgainst.ab") }}</span>
                <span class="header-title-item">{{ $t("tables.goalsDifference.ab") }}</span>
              </div>
            </div>
            <div
              v-for="(standingTeam, i) in groupStanding.standings"
              :key="standingTeam.id"
              class="standing-row relative min-w-max"
            >
              <div
                :class="[
                  'standing-row-left',
                  { 'secondary-standing': !!secondaryPositionLegend },
                  getSecondaryRowColor(i),
                ]"
              >
                <span :class="['split-standing', getRowColor(i)]"></span>
                <div class="standing-row-rank-team">
                  <div class="standing-rank">
                    <p>{{ standingTeam.rank }}</p>
                  </div>

                  <div class="standing-team-container">
                    <div class="logo-team-container">
                      <NuxtImg
                        :src="setAliasUrl(standingTeam.team.logo.url)"
                        :alt="standingTeam.team.name"
                        class="object-contain"
                        :loading="i > 6 ? 'lazy' : 'eager'"
                        width="44"
                        height="44"
                      />
                    </div>
                    <div class="hidden md:block">
                      <nuxt-link
                        :to="
                          localePath({
                            name: 'teams-team-teamName',
                            params: {
                              team: standingTeam.team.id,
                              teamName: slugify(standingTeam.team.name),
                            },
                          })
                        "
                      >
                        <h2>{{ standingTeam.team.name }}</h2>
                      </nuxt-link>
                    </div>
                    <div class="block md:hidden">
                      <nuxt-link
                        :to="
                          localePath({
                            name: 'teams-team-teamName',
                            params: {
                              team: standingTeam.team.id,
                              teamName: slugify(standingTeam.team.name),
                            },
                          })
                        "
                      >
                        {{ standingTeam.team.shortName }}
                      </nuxt-link>
                    </div>
                  </div>
                </div>
              </div>

              <div class="standing-row-data">
                <span v-if="!isKingsFirstSplit" class="standing-table-item-data !text-white">
                  {{ standingTeam.points }}
                </span>
                <span class="standing-table-item-data">{{ standingTeam.gameTotal }}</span>
                <span :class="['standing-table-item-data', { 'order-first': isKingsFirstSplit }]">
                  {{ standingTeam.gameWon }}
                </span>
                <span class="standing-table-item-data">{{ standingTeam.gamePenaltyWon }}</span>
                <span class="standing-table-item-data">{{ standingTeam.gamePenaltyLost }}</span>
                <span class="standing-table-item-data">{{ standingTeam.gameLost }}</span>
                <span class="standing-table-item-data">{{ standingTeam.goalPro }}</span>
                <span class="standing-table-item-data">{{ standingTeam.goalAgainst }}</span>
                <span class="standing-table-item-data">
                  {{ standingTeam.goalPro - standingTeam.goalAgainst }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="positionLegend" class="mt-4">
      <standings-legend :legend="positionLegend" large></standings-legend>
    </div>
    <div v-if="secondaryPositionLegend" class="mt-4">
      <standings-legend :legend="secondaryPositionLegend" large></standings-legend>
    </div>
  </section>

  <section v-if="isWorldCup" class="kql-w-container px-4">
    <div class="text-right">
      <seasons-filter
        v-model:season-id="seasonId"
        v-model:season-cluster-id="seasonClusterId"
        @change="updateStandingPhase"
      />
    </div>

    <kwc-tables-all-leagues v-if="seasonId === 37"></kwc-tables-all-leagues>

    <slider-arrows-behaviour
      v-else
      button-right-id="bracketButtonRight"
      button-left-id="bracketButtonLeft"
      :button-scroll-distance="204"
      slider-container-id="tablesSlider"
      :conditionally-render-if="isWorldCup"
    >
      <bracket-tables-world-cup :selected-season="seasonId"></bracket-tables-world-cup>
    </slider-arrows-behaviour>
  </section>

  <section v-else-if="isNations" class="kql-w-container px-4">
    <div class="text-right">
      <seasons-filter
        v-model:season-id="seasonId"
        v-model:season-cluster-id="seasonClusterId"
        @change="updateStandingPhase"
      />
    </div>

    <slider-arrows-behaviour
      button-right-id="bracketButtonRight"
      button-left-id="bracketButtonLeft"
      :button-scroll-distance="204"
      slider-container-id="tablesSlider"
      :conditionally-render-if="isNations"
    >
      <bracket-tables-nations></bracket-tables-nations>
    </slider-arrows-behaviour>
  </section>

  <section v-else-if="isGermany" class="kql-w-container">
    <div class="text-right px-4">
      <seasons-filter v-model:season-id="seasonId" v-model:season-cluster-id="seasonClusterId" />
    </div>

    <germany-bracket></germany-bracket>
  </section>

  <section v-if="!isWorldCup || (isWorldCup && seasonId !== 13)" class="py-8">
    <current-season-slider-matches section-title></current-season-slider-matches>
  </section>

  <grid-partners-competition></grid-partners-competition>
</template>

<style scoped>
.standing-group-title {
  @apply text-primary flex justify-center items-center text-[10px] gap-0.5;
}

.standing-group-name {
  @apply px-1.5 rounded border border-primary uppercase text-[10px] font-normal text-primary leading-[17px] ml-0.5;
}

.header-table-standing {
  @apply flex justify-start items-center text-xs text-gray-400 uppercase mb-2 min-w-max;
}

.standing-row {
  @apply flex justify-start items-center relative bg-gray-800 h-16 mb-1 rounded-lg;
}

.standing-row-left {
  @apply rounded-l-lg overflow-hidden bg-gray-800 sticky left-0 w-[170px] md:w-auto h-16 flex items-center;
}

.standing-row-data {
  @apply rounded-r-lg overflow-hidden bg-gray-800 h-16;
}

.standing-row-rank-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 pl-3 md:pl-4 md:text-lg font-bold w-[160px] md:w-[362px] md:min-w-[362px] md:max-w-[450px];
}

.header-standing-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 pl-3 md:pl-4 w-[170px] md:w-[362px] md:min-w-[362px] md:max-w-[450px] sticky left-0 bg-gray-900;
}

.standing-rank {
  @apply h-full w-full flex items-center justify-start text-left;
}

.secondary-standing {
  @apply before:min-w-[4px] before:h-full before:bg-gray-800 before:mr-0.5;
}

span.split-standing {
  @apply h-16 w-[4px] bg-gray-800;
}

.standing-team-container {
  @apply col-start-2 col-span-3 text-left flex justify-start gap-3 md:gap-7 items-center h-full;
}

.logo-team-container {
  @apply w-11 h-11 flex justify-center items-center;
}

.standing-table-item-data {
  @apply text-center h-full flex justify-center items-center  md:text-lg text-gray-400 font-bold;
}

.header-title-item {
  @apply justify-center flex;
}

.header-standing-data,
.standing-row-data {
  @apply grid grid-cols-[repeat(9,20px)] gap-x-7 ml-auto text-center pr-5 min-w-max;
}
</style>
